'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createTeleporter } from 'react-teleporter';
import AppBar from '@mui/material/AppBar';
import { useMediaQuery } from '@mui/material';
import { translate } from '@openclassrooms/translations';
import { HeaderContactButton, useHelpTypeFromPathname } from '@openclassrooms/common-app/contact/components';
import { styled } from '@classify/react-core-components/styles';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import Box from '@classify/react-core-components/Box';
import { useTheme } from '@classify/react-core-components/styles';
import PredictiveCRMTopBanner from '../../../predictive-crm/components/PredictiveCRMTopBanner';
import MessagingButton from '../../../messaging/MessagingButton';
import useMainNavigation from '../../hooks/useMainNavigation';
import useHeaderConfiguration from '../../hooks/useHeaderConfiguration';
import HeaderNavigationDropDown from './HeaderNavigationDropDown';
import TrainingDropDownMenu from './TrainingDropDownMenu';
import ApprenticeshipDropDownMenu from './ApprenticeshipDropDownMenu';
import MainHeaderNavigationLinkWrapper from './MainHeaderNavigationLinkWrapper';
import MainHeaderDrawer from './MainHeaderDrawer';
import MainHeaderActions from './MainHeaderActions';
import MainHeaderLogo from './MainHeaderLogo';
import HeaderSeparator from './HeaderSeparator';
import BusinessDropDownMenu from './BusinessDropDownMenu';
import SearchButton from './SearchButton';
import SkipLinks from './SkipLinks';
import { Root } from './styles';
interface User {
  id?: number;
  displayableName?: string;
  profilePicture?: string;
  organizationName?: string;
  link?: {
    profile?: string;
    logOut?: string;
    employerSpace?: string;
    parameter?: string;
  };
}
export interface Props {
  classes?: Record<string, string>;
  isNavigationHidden?: boolean;
  isNavigationCollapsed?: boolean;
  isWide?: boolean;
  additionalMenu?: ReactNode;
  user?: User & {
    notificationCount: number;
    moderationNotificationCount: number;
    messageCount: number;
    profileCompletionRate?: number | null;
  };
  onLogOut?: () => void;
}
const TeleporterWrapper = styled('div')(() => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center'
}));
const TeleporterInnerWrapper = styled('div')(({
  theme: {
    breakpoints
  }
}) => ({
  height: 44,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  '&:last-of-type': {
    transition: 'opacity 0.2s ease-in-out 0.2s, max-width 0.2s ease-in-out',
    flexGrow: 1,
    [breakpoints.up('sm')]: {
      maxWidth: '100%'
    },
    '& > a:last-of-type': {
      transition: 'all 0.2s ease-in-out'
    }
  },
  '&:not(:last-of-type)': {
    transition: 'opacity 0.2s ease-in-out, max-width 0.2s ease-in-out 0.2s',
    flexGrow: 0,
    maxWidth: 0,
    overflow: 'hidden',
    opacity: 0,
    '& > a:last-of-type': {
      opacity: 0,
      transition: 'none'
    }
  }
}));
export const HeaderSearchTeleporter = createTeleporter();
export default function MainHeader(t0) {
  const $ = _c(43);
  const {
    isWide,
    additionalMenu: t1,
    isNavigationHidden,
    onLogOut: t2
  } = t0;
  const additionalMenu = t1 === undefined ? null : t1;
  const onLogOut = t2 === undefined ? _temp : t2;
  const theme = useTheme();
  let t3;
  if ($[0] !== theme.breakpoints) {
    t3 = theme.breakpoints.down("lg");
    $[0] = theme.breakpoints;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const shouldUseSmallButton = useMediaQuery(t3);
  const headerConfig = useHeaderConfiguration();
  const {
    helpType
  } = useHelpTypeFromPathname();
  const [isTrainingMenuOpen, toggleTrainingMenu] = useState(false);
  const [isApprenticeshipMenuOpen, toggleApprenticeshipMenu] = useState(false);
  const [isBusinessMenuOpen, toggleBusinessMenu] = useState(false);
  const headerRef = useRef(null);
  const navigationConfig = useMainNavigation();
  const InnerWrapper = isWide ? "div" : ContentWrapper;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SkipLinks />;
    t5 = <PredictiveCRMTopBanner />;
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  const t6 = isWide ? "mainHeader__innerWrapper" : "mainHeader__contentWrapper";
  let t7;
  if ($[4] !== additionalMenu || $[5] !== headerConfig.isConnected) {
    t7 = <MainHeaderDrawer isConnected={headerConfig.isConnected} additionalMenu={additionalMenu} shouldShowLoginButton={true} />;
    $[4] = additionalMenu;
    $[5] = headerConfig.isConnected;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <MainHeaderLogo />;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== isApprenticeshipMenuOpen || $[9] !== isBusinessMenuOpen || $[10] !== isNavigationHidden || $[11] !== isTrainingMenuOpen || $[12] !== navigationConfig) {
    t9 = !isNavigationHidden && <nav id="main-menu-navigation" className="mainHeader__navWrapper" role="navigation" aria-label={translate("main_header.main_menu")} data-testid="mainHeaderNavigation"><div className="mainHeader__linksWrapper"><HeaderNavigationDropDown name="training" anchorEl={headerRef} isOpen={isTrainingMenuOpen} link={navigationConfig.TRAINING} toggleMenu={toggleTrainingMenu} menuComponent={TrainingDropDownMenu} />{navigationConfig.APPRENTICESHIP.shouldBeDisplayed && <HeaderNavigationDropDown name="apprenticeship" anchorEl={headerRef} isOpen={isApprenticeshipMenuOpen} link={navigationConfig.APPRENTICESHIP} toggleMenu={toggleApprenticeshipMenu} menuComponent={ApprenticeshipDropDownMenu} />}{navigationConfig.FUNDING.shouldBeDisplayed && <MainHeaderNavigationLinkWrapper {...navigationConfig.FUNDING} />}<div className="mainHeader__divider"><HeaderSeparator /></div><HeaderNavigationDropDown name="business" anchorEl={headerRef} isOpen={isBusinessMenuOpen} link={navigationConfig.BUSINESS} toggleMenu={toggleBusinessMenu} menuComponent={BusinessDropDownMenu} /></div></nav>;
    $[8] = isApprenticeshipMenuOpen;
    $[9] = isBusinessMenuOpen;
    $[10] = isNavigationHidden;
    $[11] = isTrainingMenuOpen;
    $[12] = navigationConfig;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SearchButton />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t9) {
    t11 = <HeaderSearchTeleporter.Target as={TeleporterWrapper}><TeleporterInnerWrapper>{t9}{t10}</TeleporterInnerWrapper></HeaderSearchTeleporter.Target>;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== headerConfig.counters?.messageCount || $[18] !== headerConfig.isConnected) {
    t12 = headerConfig.isConnected && <MessagingButton unreadCount={headerConfig.counters?.messageCount ?? 0} />;
    $[17] = headerConfig.counters?.messageCount;
    $[18] = headerConfig.isConnected;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== headerConfig.isConnected || $[21] !== helpType || $[22] !== shouldUseSmallButton) {
    t13 = helpType !== "none" && <Box sx={{
      ml: headerConfig.isConnected && !shouldUseSmallButton ? 2 : 1,
      mr: 1
    }}><HeaderContactButton isSmall={shouldUseSmallButton} /></Box>;
    $[20] = headerConfig.isConnected;
    $[21] = helpType;
    $[22] = shouldUseSmallButton;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = headerConfig.isConnected ? headerConfig.user : null;
  const t15 = headerConfig.counters?.notificationCount ?? 0;
  const t16 = headerConfig.counters?.moderationNotificationCount ?? 0;
  const t17 = headerConfig.counters?.messageCount ?? 0;
  let t18;
  if ($[24] !== t15 || $[25] !== t16 || $[26] !== t17) {
    t18 = {
      notificationCount: t15,
      moderationNotificationCount: t16,
      messageCount: t17
    };
    $[24] = t15;
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== headerConfig.right || $[29] !== onLogOut || $[30] !== t14 || $[31] !== t18) {
    t19 = <MainHeaderActions user={t14} counters={t18} right={headerConfig.right} onLogOut={onLogOut} />;
    $[28] = headerConfig.right;
    $[29] = onLogOut;
    $[30] = t14;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t19 || $[37] !== t7) {
    t20 = <div className="mainHeader__content">{t7}{t8}{t11}{t12}{t13}{t19}</div>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t19;
    $[37] = t7;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== InnerWrapper || $[40] !== t20 || $[41] !== t6) {
    t21 = <Root className="mainHeader__root" ref={headerRef}>{t4}{t5}<AppBar elevation={0} position="static" id="header" role="banner"><InnerWrapper className={t6}>{t20}</InnerWrapper></AppBar></Root>;
    $[39] = InnerWrapper;
    $[40] = t20;
    $[41] = t6;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  return t21;
}
function _temp() {}